import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth, googleSignInHref } from "@/lib/auth";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowRight } from "lucide-react";

const formatError = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
};

export default function SignIn() {
    const { login, register } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const returnTo = new URLSearchParams(location.search).get("return") || "/passport";

    const [mode, setMode] = useState("signin"); // signin | register
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (mode === "signin") {
                await login(email, password);
                toast.success("Welcome back.");
            } else {
                await register(email, password, name);
                toast.success("Account created. Ride on.");
            }
            navigate(returnTo);
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || err.message || "Authentication failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-[80vh] bg-[var(--bg)] flex items-center justify-center px-6 py-16">
            <div className="w-full max-w-md">
                <div className="text-center mb-10">
                    <div className="eyebrow mb-4">Rider Account</div>
                    <h1 className="font-display font-black uppercase text-[var(--fg)] text-4xl md:text-5xl leading-none tracking-tight">
                        {mode === "signin" ? "Sign in." : "Create account."}
                    </h1>
                    <p className="mt-4 text-[var(--fg-muted)] font-body text-sm">
                        Track your ridden stops, upload your own photos, build your rider journal.
                    </p>
                </div>

                {/* Google button */}
                <a
                    href={googleSignInHref(returnTo)}
                    data-testid="google-signin-btn"
                    className="flex items-center justify-center gap-3 w-full bg-white text-black px-6 py-4 font-display font-bold uppercase tracking-[0.18em] text-sm hover:bg-neutral-100 transition-colors"
                >
                    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                    </svg>
                    Continue with Google
                </a>

                <div className="flex items-center gap-3 my-6">
                    <div className="flex-1 h-px bg-[var(--border-hair)]" />
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--fg-dim)] font-body">or use email</div>
                    <div className="flex-1 h-px bg-[var(--border-hair)]" />
                </div>

                <form onSubmit={onSubmit} className="space-y-4" data-testid="auth-form">
                    {mode === "register" && (
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--fg-dim)] font-body mb-2">Rider name</div>
                            <div className="relative">
                                <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--fg-dim)]" />
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    autoComplete="name"
                                    data-testid="auth-name-input"
                                    className="w-full bg-[var(--card-bg)] border border-[var(--border-hair)] text-[var(--fg)] pl-11 pr-4 py-3 font-body focus:border-[#E87722] focus:outline-none"
                                    placeholder="Your riding name"
                                />
                            </div>
                        </label>
                    )}
                    <label className="block">
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--fg-dim)] font-body mb-2">Email</div>
                        <div className="relative">
                            <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--fg-dim)]" />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="email"
                                required
                                data-testid="auth-email-input"
                                className="w-full bg-[var(--card-bg)] border border-[var(--border-hair)] text-[var(--fg)] pl-11 pr-4 py-3 font-body focus:border-[#E87722] focus:outline-none"
                                placeholder="you@example.com"
                            />
                        </div>
                    </label>
                    <label className="block">
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--fg-dim)] font-body mb-2">Password</div>
                        <div className="relative">
                            <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--fg-dim)]" />
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                                required
                                minLength={mode === "register" ? 8 : undefined}
                                data-testid="auth-password-input"
                                className="w-full bg-[var(--card-bg)] border border-[var(--border-hair)] text-[var(--fg)] pl-11 pr-4 py-3 font-body focus:border-[#E87722] focus:outline-none"
                                placeholder={mode === "register" ? "At least 8 characters" : "••••••••"}
                            />
                        </div>
                    </label>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="auth-submit-btn"
                        className="w-full inline-flex items-center justify-center gap-3 bg-[#E87722] text-black px-6 py-4 font-display font-bold uppercase tracking-[0.2em] text-sm hover:bg-[#F28C38] transition-colors disabled:opacity-60"
                    >
                        {submitting ? "Working…" : mode === "signin" ? "Sign in" : "Create account"}
                        <ArrowRight size={16} strokeWidth={2.5} />
                    </button>
                </form>

                <div className="text-center mt-8 text-sm text-[var(--fg-muted)] font-body">
                    {mode === "signin" ? (
                        <>
                            No account?{" "}
                            <button data-testid="auth-toggle-register" onClick={() => setMode("register")} className="text-[#E87722] hover:underline">
                                Create one
                            </button>
                        </>
                    ) : (
                        <>
                            Already have an account?{" "}
                            <button data-testid="auth-toggle-signin" onClick={() => setMode("signin")} className="text-[#E87722] hover:underline">
                                Sign in
                            </button>
                        </>
                    )}
                </div>

                <div className="text-center mt-6">
                    <Link to="/" className="text-xs uppercase tracking-[0.24em] text-[var(--fg-dim)] font-body hover:text-[var(--fg)]">
                        ← Back to home
                    </Link>
                </div>
            </div>
        </div>
    );
}
